import { DateTime } from "luxon";
const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const to12Hour = (time24) => {
  if (!time24) return "";
  const [h, m] = time24.split(":").map(Number);
  if (isNaN(h) || isNaN(m)) return "";
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${m.toString().padStart(2, "0")} ${period}`;
};
const to24Hour = (time12) => {
  if (!time12) return "";
  const parts = time12.trim().split(" ");
  const time = parts[0];
  const modifier = parts[1];
  let [h, m] = time.split(":").map(Number);
  if (isNaN(h) || isNaN(m)) return "";
  if (modifier === "PM" && h !== 12) h += 12;
  if (modifier === "AM" && h === 12) h = 0;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
};
const parseWeeklyHours = (officeHoursJson) => {
  const defaultMap = Object.fromEntries(WEEKDAYS.map((d) => [d, "Closed"]));
  if (!officeHoursJson) return defaultMap;
  let parsed;
  try {
    parsed = JSON.parse(officeHoursJson);
  } catch (e) {
    return defaultMap;
  }
  if (!Array.isArray(parsed)) return defaultMap;
  parsed.forEach(({ days, from, to }) => {
    if (!Array.isArray(days) || !from || !to) return;

    const timeRe = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRe.test(from) || !timeRe.test(to)) return;

    const range = `${to12Hour(from)} – ${to12Hour(to)}`;
    days.forEach((day) => {
      if (defaultMap.hasOwnProperty(day)) defaultMap[day] = range;
    });
  });
  return defaultMap;
};
const normalizeTimeZone = (tz) => {
  if (!tz) return null;
  return tz
    .split(",")[0]
    .trim()
    .split(/\s{2,}/)[0]
    .trim();
};
const isValidTimeZone = (tz) => {
  if (!tz) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch (e) {
    return false;
  }
};
export const getOfficeStatus = (officeHoursJson, vendorTz, userTz = null) => {
  const cleanVendorTz = normalizeTimeZone(vendorTz);
  if (!cleanVendorTz || !isValidTimeZone(cleanVendorTz)) {
    cleanVendorTz = "UTC";
  }
  const weekly = parseWeeklyHours(officeHoursJson);
  let vendorNow;
  try {
    vendorNow = new Date(
      new Date().toLocaleString("en-US", { timeZone: cleanVendorTz })
    );
  } catch (e) {
    console.error("Failed to get time in vendor timezone:", cleanVendorTz, e);
    vendorNow = new Date(); // fallback
  }
  const vendorDayName = vendorNow.toLocaleString("en-US", {
    weekday: "long",
    timeZone: cleanVendorTz,
  });
  const todayHours = weekly[vendorDayName] || "Closed";
  const isClosedDay = todayHours === "Closed";
  let isOpen = false;
  if (!isClosedDay) {
    const [open12, close12] = todayHours.split(" – ").map((s) => s.trim());
    const open24 = to24Hour(open12);
    const close24 = to24Hour(close12);
    if (open24 && close24) {
      const base = new Date(vendorNow);
      base.setHours(0, 0, 0, 0); // midnight
      const openDate = new Date(base);
      const [oh, om] = open24.split(":").map(Number);
      openDate.setHours(oh, om, 0, 0);
      const closeDate = new Date(base);
      const [ch, cm] = close24.split(":").map(Number);
      closeDate.setHours(ch, cm, 0, 0);
      const nowDate = new Date(base);
      nowDate.setHours(vendorNow.getHours(), vendorNow.getMinutes(), 0, 0);
      isOpen = nowDate >= openDate && nowDate <= closeDate;
    }
  }
  return {
    isOpen,
    today: {
      day: vendorDayName,
      hours: todayHours,
    },
    weekly,
    vendorNow,
    vendorTz: cleanVendorTz,
    userTz: userTz || Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};
export const getLastOnlineDisplay = (timestamp, userTimezone, myTimezone) => {
  if (!timestamp || !userTimezone || !myTimezone) return "";
  // Remove extra comma and spaces from timezone names like "America/Los_Angeles, Pacific Time"
  const cleanUserTz = userTimezone.split(",")[0].trim();
  const cleanMyTz = myTimezone.split(",")[0].trim();
  // Parse last online time in user's timezone
  const userTime = DateTime.fromFormat(timestamp, "yyyy-MM-dd HH:mm:ss", {
    zone: cleanUserTz,
  });
  if (!userTime.isValid) {
    console.error("Invalid date/time:", userTime.invalidExplanation);
    return "Last online: Invalid time";
  }
  // Convert to my timezone
  const myTime = userTime.setZone(cleanMyTz);
  // Get current time in my timezone
  const now = DateTime.now().setZone(cleanMyTz);
  // Compare
  if (myTime.hasSame(now, "day")) {
    return `${myTime.toFormat("h:mm a")}`;
  } else if (myTime.plus({ days: 1 }).hasSame(now, "day")) {
    return "Yesterday";
  } else {
    return `${myTime.toFormat("MMM d, yyyy, h:mm a")}`;
  }
};
export const formatUsDate = (dateInput) => {
    if (!dateInput) return "N/A";
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return "N/A";
    return new Intl.DateTimeFormat("en-US", {
      month: "long", // "February"
      day: "2-digit", // "05"
      year: "numeric", // "2026"
    }).format(date);
  };